import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor - attach token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('savenes_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for token refresh + auto-retry on transient network errors
// Backend hot-reloads / platform cold-starts can take up to ~8 seconds, so we retry
// 3 times with exponential backoff: 1s, 2s, 4s (covers most restart windows).
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    const isNetworkOrTransient =
      !error.response ||
      error.code === 'ECONNABORTED' ||
      error.code === 'ERR_NETWORK' ||
      [502, 503, 504].includes(error.response?.status);

    if (isNetworkOrTransient && originalRequest) {
      originalRequest._netRetries = (originalRequest._netRetries || 0) + 1;
      if (originalRequest._netRetries <= 3) {
        const backoff = 1000 * Math.pow(2, originalRequest._netRetries - 1); // 1s, 2s, 4s
        await new Promise((r) => setTimeout(r, backoff));
        try {
          return await api(originalRequest);
        } catch (_) {
          // fall through to normal error handling on final failure
        }
      }
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const token = localStorage.getItem('savenes_token');
        const refreshRes = await axios.post(`${API_URL}/api/auth/refresh`, {}, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (refreshRes.data.access_token) {
          localStorage.setItem('savenes_token', refreshRes.data.access_token);
          originalRequest.headers.Authorization = `Bearer ${refreshRes.data.access_token}`;
        }
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('savenes_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// Helper to format API error
export function formatApiError(error) {
  // Network errors (server restart, CORS preflight blocked, offline) have no response
  if (error && !error.response) {
    if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
      return 'Could not reach the server. Please check your connection and try again.';
    }
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return 'Request timed out. The server may be restarting — please try again in a few seconds.';
    }
    return error.message || 'Unable to reach the server. Please try again.';
  }
  const detail = error.response?.data?.detail;
  if (detail == null) {
    // HTTP error with no JSON body (500/502/504 etc.)
    const status = error.response?.status;
    if (status >= 500) return 'The server had a problem. Please try again in a moment.';
    if (status === 502 || status === 503 || status === 504) return 'The server is temporarily unavailable. Please retry in a few seconds.';
    return 'Something went wrong. Please try again.';
  }
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

// Auth API
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  refresh: () => api.post('/auth/refresh'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
  setupMFA: () => api.post('/auth/mfa/setup'),
  verifyMFA: (code) => api.post('/auth/mfa/verify', { code })
};

// Organization API
export const orgApi = {
  list: () => api.get('/organizations'),
  get: (id) => api.get(`/organizations/${id}`),
  create: (data) => api.post('/organizations', data),
  update: (id, data) => api.put(`/organizations/${id}`, data),
  remove: (id) => api.delete(`/organizations/${id}`),
  getUsers: (id) => api.get(`/organizations/${id}/users`)
};

// Admin API
export const adminApi = {
  createUser: (data) => api.post('/admin/users', data),
  users: () => api.get('/admin/users'),
  getUser: (id) => api.get(`/admin/users/${id}`),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  setPassword: (id, data) => api.post(`/admin/users/${id}/set-password`, data),
  resetPassword: (id) => api.post(`/admin/users/${id}/reset-password`),
  deletePatient: (id) => api.delete(`/admin/patients/${id}`),
  archivePatient: (id) => api.put(`/patients/${id}/archive`),
  unarchivePatient: (id) => api.put(`/patients/${id}/unarchive`),
};

// Executive Insights API
export const insightsApi = {
  getExecutive: () => api.get('/insights/executive')
};

// AI Recommendations API
export const aiRecommendationsApi = {
  get: (context = 'general') => api.get('/ai/recommendations', { params: { context } })
};

// CQM Drill-down API
export const cqmDrillApi = {
  getMeasureDetail: (measureId) => api.get(`/cqm/measure/${measureId}`)
};

// Physician API
export const physicianApi = {
  getVitalsSummary: () => api.get('/physician/patient-vitals-summary')
};

// Patient API
export const patientApi = {
  list: (params = {}) => api.get('/patients', { params }),
  get: (id) => api.get(`/patients/${id}`),
  create: (data) => api.post('/patients', data),
  update: (id, data) => api.put(`/patients/${id}`, data),
  getTimeline: (id) => api.get(`/patients/${id}/timeline`),
  getCareGaps: (id) => api.get(`/patients/${id}/care-gaps`),
  getFiles: (id) => api.get(`/patients/${id}/files`),
  createFolder: (id, name) => api.post(`/patients/${id}/folders`, { name }),
  moveFile: (id, fileId, folderId) => api.put(`/patients/${id}/files/${fileId}/move`, { folder_id: folderId }),
  uploadFile: (id, payload) => api.post(`/patients/${id}/files/upload`, payload),
  getUpload: (id, fileId) => api.get(`/patients/${id}/files/uploads/${fileId}`),
  deleteUpload: (id, fileId) => api.delete(`/patients/${id}/files/uploads/${fileId}`),
  exportCsv: (search = '') => api.get(`/patients/export.csv${search ? `?search=${encodeURIComponent(search)}` : ''}`, { responseType: 'text' }),
  uploadExcel: (formData) => api.post('/patients/upload-excel', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  recordPdf: (id, payload) => api.post(`/patients/${id}/record-pdf`, payload, {
    // Use blob when downloading a PDF so we can build an object URL; axios
    // will parse JSON as-is when the server sends application/json for the
    // email path.
    responseType: payload?.delivery === 'email' ? 'json' : 'blob',
  }),
};

// Vitals API
export const vitalApi = {
  list: (patientId) => api.get('/vitals', { params: { patient_id: patientId } }),
  create: (data) => api.post('/vitals', data)
};

// Medication API
export const medicationApi = {
  list: (patientId, activeOnly = true) => api.get('/medications', { params: { patient_id: patientId, active_only: activeOnly } }),
  create: (data) => api.post('/medications', data)
};

// Lab API
export const labApi = {
  list: (patientId, status = null) => api.get('/labs', { params: { patient_id: patientId, status } }),
  order: (data) => api.post('/labs/order', data)
};

// Appointment API
export const appointmentApi = {
  list: (params = {}) => api.get('/appointments', { params }),
  create: (data) => api.post('/appointments', data),
  update: (id, status) => api.patch(`/appointments/${id}`, null, { params: { status } })
};

// Encounter API
export const encounterApi = {
  list: (params = {}) => api.get('/encounters', { params }),
  create: (data) => api.post('/encounters', data)
};

// Clinical Notes API
export const noteApi = {
  list: (encounterId) => api.get('/notes', { params: { encounter_id: encounterId } }),
  create: (data) => api.post('/notes', data),
  sign: (id) => api.post(`/notes/${id}/sign`)
};

// AI API
export const aiApi = {
  summarizeNote: (content) => api.post('/ai/summarize-note', { content }),
  generateCarePlan: (content) => api.post('/ai/generate-care-plan', { content }),
  chat: (content, context = null) => api.post('/ai/chat', { content, context })
};

// Analytics API
export const analyticsApi = {
  orgOverview: () => api.get('/analytics/org-overview'),
  provider: (providerId) => api.get(`/analytics/provider/${providerId}`)
};

// Messages API
export const messageApi = {
  inbox: () => api.get('/messages/inbox'),
  send: (data) => api.post('/messages', data),
  markRead: (id) => api.patch(`/messages/${id}/read`)
};

// Users API
export const userApi = {
  list: (role = null) => api.get('/users', { params: { role } })
};

// Notifications API
export const notificationApi = {
  list: () => api.get('/notifications'),
  markAllRead: () => api.post('/notifications/mark-read'),
  markRead: (id) => api.patch(`/notifications/${id}/read`)
};

// EHR Integration API
export const ehrApi = {
  getPatientFHIR: (patientId) => api.get(`/ehr/patient/${patientId}/fhir`),
  getPatientBundle: (patientId) => api.get(`/ehr/patient/${patientId}/bundle`),
  exportData: (format = 'fhir') => api.get('/ehr/export', { params: { format } })
};

// EHR Settings API
export const ehrSettingsApi = {
  getAll: () => api.get('/settings/ehr-integrations'),
  update: (provider, data) => api.put(`/settings/ehr-integrations/${provider}`, data),
  testConnection: (provider) => api.post(`/settings/ehr-integrations/${provider}/test`)
};

// Population Health API
export const populationHealthApi = {
  getAnalytics: () => api.get('/analytics/population-health'),
  getPredictions: () => api.post('/ai/population-predictions')
};

// Reports API
export const reportApi = {
  generate: (data) => api.post('/reports/generate', data),
  list: () => api.get('/reports'),
  get: (id) => api.get(`/reports/${id}`),
  update: (id, data) => api.put(`/reports/${id}`, data),
  delete: (id) => api.delete(`/reports/${id}`),
  schedule: (id, data) => api.put(`/reports/${id}/schedule`, data),
  toWidget: (id, data) => api.post(`/reports/${id}/to-widget`, data),
};

// AI Clinical Decision Support
export const cdsApi = {
  analyze: (patientId) => api.post('/ai/clinical-decision-support', { patient_id: patientId })
};

// Widget API
export const widgetApi = {
  getTemplates: () => api.get('/widgets/templates'),
  getDashboard: () => api.get('/widgets/dashboard'),
  addWidget: (data) => api.post('/widgets/dashboard', data),
  updateWidget: (id, data) => api.put(`/widgets/dashboard/${id}`, data),
  deleteWidget: (id) => api.delete(`/widgets/dashboard/${id}`),
  getData: (widgetType, dataSource) => api.get(`/widgets/data/${widgetType}/${dataSource}`)
};

// Audit Log API
export const auditLogApi = {
  list: (params = {}) => api.get('/audit-logs', { params }),
  getActions: () => api.get('/audit-logs/actions')
};

// Multi-tenant API
export const tenantApi = {
  verifyIsolation: () => api.get('/tenant/verify')
};

// CQM API
export const cqmApi = {
  getDashboard: () => api.get('/cqm/dashboard')
};

// FHIR Webhooks API
export const fhirWebhookApi = {
  list: () => api.get('/fhir/webhooks'),
  register: (data) => api.post('/fhir/webhooks', data),
  update: (id, data) => api.put(`/fhir/webhooks/${id}`, data),
  remove: (id) => api.delete(`/fhir/webhooks/${id}`),
  test: (id) => api.post(`/fhir/webhooks/${id}/test`),
  deliveries: (id) => api.get(`/fhir/webhooks/${id}/deliveries`)
};

// Audit Analytics API
export const auditAnalyticsApi = {
  get: () => api.get('/audit-logs/analytics')
};

// Patient Self-Service API
export const patientSelfServiceApi = {
  updateProfile: (data) => api.put('/patient/profile', data),
  getRecords: () => api.get('/patient/records'),
  requestRefill: (data) => api.post('/patient/refill-request', data),
  getRefillRequests: () => api.get('/patient/refill-requests'),
  sendMessage: (data) => api.post('/patient/messages', data),
  getMessages: () => api.get('/patient/messages'),
  getBilling: () => api.get('/patient/billing'),
  downloadRecordPdf: () => api.get('/patient/record-pdf', { responseType: 'blob' }),
  requestDocument: (data) => api.post('/patient/document-requests', data),
  listDocumentRequests: () => api.get('/patient/document-requests'),
};

// Claims API
export const claimApi = {
  list: (params = {}) => api.get('/claims', { params }),
  create: (data) => api.post('/claims', data),
  submit: (id) => api.post(`/claims/${id}/submit`),
  update: (id, data) => api.patch(`/claims/${id}`, data),
  appeal: (id, reason) => api.post(`/claims/${id}/appeal`, null, { params: { appeal_reason: reason } }),
  tracker: () => api.get('/claims/tracker'),
  pipeline: (stage) => api.get(`/claims/pipeline/${stage}`),
  detail: (id) => api.get(`/claims/${id}/detail`),
  seedDemo: () => api.post('/claims/seed-demo'),
  predictDenial: (data) => api.post('/claims/predict-denial', data),
};

// Scheduling API
export const schedulingApi = {
  getAvailableSlots: (providerId, date) => api.get('/scheduling/available-slots', { params: { provider_id: providerId, date } }),
  selfSchedule: (data) => api.post('/scheduling/self-schedule', data)
};

// Tickets API
export const ticketApi = {
  list: (status = null) => api.get('/tickets', { params: { status } }),
  get: (id) => api.get(`/tickets/${id}`),
  create: (data) => api.post('/tickets', data),
  respond: (id, response) => api.post(`/tickets/${id}/respond`, { response }),
  updateStatus: (id, status) => api.patch(`/tickets/${id}/status`, null, { params: { status } })
};

// Prescriptions API
export const prescriptionApi = {
  list: (params = {}) => api.get('/prescriptions', { params }),
  create: (data) => api.post('/prescriptions', data),
  send: (id) => api.post(`/prescriptions/${id}/send`),
  checkInteractions: (data) => api.post('/prescriptions/check-interactions', data)
};

export default api;

// Denial Prevention API
export const denialPreventionApi = {
  dashboard: () => api.get('/denial-prevention/dashboard'),
  seedDemo: () => api.post('/denial-prevention/seed-demo'),
};

// User Management API
export const userManagementApi = {
  list: (params = {}) => api.get('/admin/users', { params }),
  get: (id) => api.get(`/admin/users/${id}`),
  create: (data) => api.post('/admin/users', data),
  update: (id, data) => api.put(`/admin/users/${id}`, data),
  delete: (id) => api.delete(`/admin/users/${id}`),
  resetPassword: (id) => api.post(`/admin/users/${id}/reset-password`),
  bulkAction: (data) => api.post('/admin/users/bulk-action', data),
};

// CRM API
export const crmApi = {
  dashboard: () => api.get('/crm/dashboard'),
  pipeline: () => api.get('/crm/pipeline'),
  leads: {
    list: (params = {}) => api.get('/crm/leads', { params }),
    get: (id) => api.get(`/crm/leads/${id}`),
    create: (data) => api.post('/crm/leads', data),
    update: (id, data) => api.put(`/crm/leads/${id}`, data),
    delete: (id) => api.delete(`/crm/leads/${id}`),
  },
  campaigns: {
    list: (params = {}) => api.get('/crm/campaigns', { params }),
    create: (data) => api.post('/crm/campaigns', data),
    update: (id, data) => api.put(`/crm/campaigns/${id}`, data),
    delete: (id) => api.delete(`/crm/campaigns/${id}`),
  },
  templates: {
    list: (params = {}) => api.get('/crm/templates', { params }),
    create: (data) => api.post('/crm/templates', data),
    update: (id, data) => api.put(`/crm/templates/${id}`, data),
    delete: (id) => api.delete(`/crm/templates/${id}`),
  },
  seedDemo: () => api.post('/crm/seed-demo'),
};

// MyChart API
export const myChartApi = {
  status: () => api.get('/mychart/status'),
  configure: (data) => api.post('/mychart/configure', data),
  sync: () => api.post('/mychart/sync'),
  patients: () => api.get('/mychart/patients'),
  push: (patientId) => api.post(`/mychart/push/${patientId}`),
  fhirResource: (type, patientId) => api.get(`/mychart/fhir/${type}`, { params: { patient_id: patientId } }),
};


// Forms API
// Long-running AI calls now use the BACKGROUND-JOB pattern: kick off a job,
// poll /api/ai-jobs/{id} until done. Avoids the K8s ingress 60s timeout that
// was killing PDF/image AI extraction. _aiCall keeps the connectivity
// banner suppressed for the entire duration so it never flips to "offline".
const _aiCall = async (fn) => {
  window.__savenesAiBusy = true;
  try {
    return await fn();
  } finally {
    window.__savenesAiBusy = false;
  }
};
const _aiOpts = { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 };

// Poll /api/ai-jobs/{job_id} until done/error. Resolves with the result or rejects.
// `shouldAbort()` is checked between polls — return true to silently stop polling
// (useful when the user dismisses the dialog; the BACKEND keeps running and a
// notification will surface the result instead).
const _pollAiJob = async (jobId, { intervalMs = 3000, maxMs = 600000, onProgress, shouldAbort } = {}) => {
  const start = Date.now();
  await new Promise((r) => setTimeout(r, 1000));
  while (Date.now() - start < maxMs) {
    if (shouldAbort && shouldAbort()) {
      const e = new Error('Polling aborted by client');
      e.aborted = true;
      throw e;
    }
    const { data } = await api.get(`/ai-jobs/${jobId}`, { timeout: 15000 });
    if (onProgress) {
      onProgress({
        message: data.progress || '',
        total: data.total,
        completed: data.completed,
      });
    }
    if (data.status === 'done') return { data: data.result };
    if (data.status === 'error') {
      const err = new Error(data.error || 'AI job failed');
      err.response = { data: { detail: data.error } };
      throw err;
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  throw new Error('AI job timed out');
};

// Returns { jobId, promise } so the caller can show "Run in background" UI:
// closing the dialog can call shouldAbort() to stop polling client-side
// while the backend keeps running and posts a notification when done.
const _aiJobStart = async (formData, url) => {
  const start = await api.post(url, formData, _aiOpts);
  const jobId = start.data?.job_id;
  if (!jobId) throw new Error('Backend did not return job_id');
  return jobId;
};

const _aiJobCall = async (formData, url, onProgress, shouldAbort) => _aiCall(async () => {
  const jobId = await _aiJobStart(formData, url);
  return _pollAiJob(jobId, { onProgress, shouldAbort });
});

export const formsApi = {
  list: (params = {}) => api.get('/forms', { params }),
  get: (id) => api.get(`/forms/${id}`),
  create: (data) => api.post('/forms', data),
  update: (id, data) => api.put(`/forms/${id}`, data),
  delete: (id) => api.delete(`/forms/${id}`),
  aiGenerate: (formData) => _aiCall(() => api.post('/forms/ai-generate', formData, { ..._aiOpts, timeout: 180000 })),
  aiAnalyzeTemplate: (formData, onProgress, shouldAbort) => _aiJobCall(formData, '/forms/ai-analyze-template', onProgress, shouldAbort),
  aiGeneratePdfLayout: (formData, onProgress, shouldAbort) => _aiJobCall(formData, '/forms/ai-generate-pdf-layout', onProgress, shouldAbort),
  getAiImport: (importId) => api.get(`/ai-form-imports/${importId}`),
  savePrintTemplate: (formId, data) => api.post(`/forms/${formId}/print-template`, data),
  getPrintTemplate: (formId) => api.get(`/forms/${formId}/print-template`),
  submit: (formId, data) => api.post(`/forms/${formId}/submit`, data),
  submissions: (formId, params = {}) => api.get(`/forms/${formId}/submissions`, { params }),
  submissionsTable: (formId) => api.get(`/forms/${formId}/submissions/table`),
  submissionsCsv: (formId) => api.get(`/forms/${formId}/submissions/csv`, { responseType: 'blob' }),
  getSubmission: (submissionId) => api.get(`/form-submissions/${submissionId}`),
  updateSubmission: (submissionId, data) => api.put(`/form-submissions/${submissionId}`, data),
  deleteSubmission: (submissionId) => api.delete(`/form-submissions/${submissionId}`),
  downloadSubmissionPdf: (submissionId) => api.get(`/form-submissions/${submissionId}/pdf`, { responseType: 'blob' }),
  chartData: (formId, params = {}) => api.get(`/forms/${formId}/chart-data`, { params }),
  patientForms: (patientId) => api.get(`/patients/${patientId}/forms`),
  exportPdf: (formId, options = {}) => api.post(`/forms/${formId}/export-pdf`, options, { responseType: 'blob' }),
  share: (formId, data) => api.post(`/forms/${formId}/share`, data),
  unshare: (formId) => api.delete(`/forms/${formId}/share`),
  auditTrail: (formId) => api.get(`/forms/${formId}/audit-trail`),
  workflows: {
    list: () => api.get('/forms/workflows'),
    create: (data) => api.post('/forms/workflows', data),
    delete: (id) => api.delete(`/forms/workflows/${id}`),
    start: (id, data) => api.post(`/forms/workflows/${id}/start`, data),
    instances: (params = {}) => api.get('/forms/workflows/instances', { params }),
  },
};

export const signatureApi = {
  create: (data) => api.post('/signature-requests', data),
  list: () => api.get('/signature-requests'),
  getByToken: (token) => api.get(`/sign/${token}`),
  submit: (token, data) => api.post(`/sign/${token}`, data),
};

// Module Management API
export const moduleApi = {
  list: () => api.get('/modules'),
  toggle: (key, enabled) => api.put(`/modules/${key}`, { enabled }),
};

// Custom Dashboards API
export const customDashboardApi = {
  list: () => api.get('/custom-dashboards'),
  get: (id) => api.get(`/custom-dashboards/${id}`),
  create: (data) => api.post('/custom-dashboards', data),
  update: (id, data) => api.put(`/custom-dashboards/${id}`, data),
  delete: (id) => api.delete(`/custom-dashboards/${id}`),
  setHomepage: (id) => api.put(`/custom-dashboards/${id}/set-homepage`),
  unsetHomepage: (id) => api.put(`/custom-dashboards/${id}/unset-homepage`),
  homepage: () => api.get('/custom-dashboards/homepage'),
  share: (id, userIds) => api.post(`/custom-dashboards/${id}/share`, { user_ids: userIds }),
  shared: () => api.get('/shared-dashboards'),
};

// Patient Tabs API
export const patientTabApi = {
  list: () => api.get('/patient-tabs'),
  create: (data) => api.post('/patient-tabs', data),
  delete: (id) => api.delete(`/patient-tabs/${id}`),
};

// Patient Custom Fields API
export const patientFieldApi = {
  list: () => api.get('/patient-custom-fields'),
  create: (data) => api.post('/patient-custom-fields', data),
  delete: (id) => api.delete(`/patient-custom-fields/${id}`),
};

// Claims API
export const claimsApi = {
  createManual: (data) => api.post('/claims/manual', data),
};

// Leads API
export const leadsApi = {
  bulkAction: (data) => api.post('/leads/bulk-action', data),
};

// Tasks API
export const taskApi = {
  list: (params = {}) => api.get('/tasks', { params }),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
};

// Users API (for assignment)
export const usersApi = {
  list: (params = {}) => api.get('/users', { params }),
};

// Form Assignments API
export const formAssignmentApi = {
  create: (data) => api.post('/form-assignments', data),
  list: (params = {}) => api.get('/form-assignments', { params }),
  delete: (id) => api.delete(`/form-assignments/${id}`),
  rollover: () => api.post('/form-assignments/rollover'),
};

// Form Drafts API
export const formDraftApi = {
  save: (data) => api.post('/form-drafts', data),
  list: () => api.get('/form-drafts'),
  get: (id) => api.get(`/form-drafts/${id}`),
  delete: (id) => api.delete(`/form-drafts/${id}`),
};

// Last-location (resume-where-you-left-off)
export const lastLocationApi = {
  get: () => api.get('/auth/last-location'),
  update: (payload) => api.put('/auth/last-location', payload),
};

// Sidebar nav preferences — custom order, favorites, and hidden items
export const navPrefsApi = {
  get: () => api.get('/auth/nav-prefs'),
  update: (prefs) => api.put('/auth/nav-prefs', prefs),
};

// Reminders API
export const reminderApi = {
  send: (data) => api.post('/reminders/send', data),
};

// Admin Dashboard Widgets
export const adminWidgetsApi = {
  get: () => api.get('/dashboard/admin-widgets'),
};

// Stripe Subscription
export const stripeApi = {
  plans: () => api.get('/stripe/plans'),
  createCheckout: (data) => api.post('/stripe/checkout', data),
  status: (sessionId) => api.get(`/stripe/status/${sessionId}`),
  subscription: () => api.get('/stripe/subscription'),
  cancel: () => api.post('/stripe/subscription/cancel'),
};

// Integration Settings (Stripe + Resend)
export const integrationSettingsApi = {
  get: (provider) => api.get(`/settings/integrations/${provider}`),
  save: (provider, data) => api.put(`/settings/integrations/${provider}`, data),
  test: (provider) => api.post(`/settings/integrations/${provider}/test`),
};

// Analytics AI Narrative
export const aiNarrativeApi = {
  generate: (scope, contextData) => api.post('/ai/analytics-narrative', { scope, context_data: contextData }),
};

// =====================================================================
// Patient Incident Reports
// =====================================================================
export const incidentsApi = {
  listTypes: () => api.get('/incidents/types'),
  list: (params = {}) => api.get('/incidents', { params }),
  analytics: (days = 30) => api.get('/incidents/analytics', { params: { days } }),
  get: (id) => api.get(`/incidents/${id}`),
  create: (data) => api.post('/incidents', data),
  updateStatus: (id, status, response_notes) =>
    api.patch(`/incidents/${id}/status`, { status, response_notes }),
};

// =====================================================================
// Learning Management System (LMS)
// =====================================================================
export const lmsApi = {
  listCourses: (params = {}) => api.get('/lms/courses', { params }),
  getCourse: (id) => api.get(`/lms/courses/${id}`),
  createCourse: (data) => api.post('/lms/courses', data),
  updateCourse: (id, data) => api.put(`/lms/courses/${id}`, data),
  deleteCourse: (id) => api.delete(`/lms/courses/${id}`),
  createLesson: (courseId, data) => api.post(`/lms/courses/${courseId}/lessons`, data),
  updateLesson: (id, data) => api.put(`/lms/lessons/${id}`, data),
  deleteLesson: (id) => api.delete(`/lms/lessons/${id}`),
  reorderLesson: (id, order) => api.put(`/lms/lessons/${id}/reorder`, { order }),
  enroll: (courseId) => api.post(`/lms/courses/${courseId}/enroll`),
  markLesson: (lessonId, completed = true, quiz_score) =>
    api.post(`/lms/lessons/${lessonId}/progress`, { completed, quiz_score }),
  myEnrollments: () => api.get('/lms/me/enrollments'),
  myCertificates: () => api.get('/lms/me/certificates'),
  courseEnrollments: (courseId) => api.get(`/lms/courses/${courseId}/enrollments`),
  upload: (bucket, file, onProgress) => {
    const fd = new FormData();
    fd.append('bucket', bucket);
    fd.append('file', file);
    return api.post('/lms/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
  },
};

// =====================================================================
// Knowledge Base
// =====================================================================
export const knowledgeApi = {
  listCategories: () => api.get('/knowledge/categories'),
  createCategory: (data) => api.post('/knowledge/categories', data),
  updateCategory: (id, data) => api.put(`/knowledge/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/knowledge/categories/${id}`),
  listArticles: (params = {}) => api.get('/knowledge/articles', { params }),
  getArticle: (idOrSlug) => api.get(`/knowledge/articles/${idOrSlug}`),
  createArticle: (data) => api.post('/knowledge/articles', data),
  updateArticle: (id, data) => api.put(`/knowledge/articles/${id}`, data),
  deleteArticle: (id) => api.delete(`/knowledge/articles/${id}`),
};

// =====================================================================
// Community Forum
// =====================================================================
export const forumApi = {
  listCategories: () => api.get('/forum/categories'),
  createCategory: (data) => api.post('/forum/categories', data),
  updateCategory: (id, data) => api.put(`/forum/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/forum/categories/${id}`),
  listThreads: (catId, sort = 'recent') =>
    api.get(`/forum/categories/${catId}/threads`, { params: { sort } }),
  createThread: (data) => api.post('/forum/threads', data),
  getThread: (id) => api.get(`/forum/threads/${id}`),
  updateThread: (id, data) => api.put(`/forum/threads/${id}`, data),
  deleteThread: (id) => api.delete(`/forum/threads/${id}`),
  createPost: (data) => api.post('/forum/posts', data),
  updatePost: (id, body_html) => api.put(`/forum/posts/${id}`, { body_html }),
  deletePost: (id) => api.delete(`/forum/posts/${id}`),
  voteThread: (id, value) => api.post(`/forum/threads/${id}/vote`, { value }),
  votePost: (id, value) => api.post(`/forum/posts/${id}/vote`, { value }),
};
